import {
  FetchNodesFunction,
  HierarchyType,
  nodeType,
  SearchNodesFunction,
  treeIdPrefix,
  TreeNode,
} from "@fidelix/fx-miranda";
import { assetsApiRequest } from "~api/api-request";
import { ApiResponse } from "~api/api-response.model";
import { t } from "@lingui/core/macro";
import { getActiveRoute, MainRoute, mainRoutes } from "../common/nav-bar";
import { NodeWithHierarchyDTO, TreeDTO } from "./tree.types";
import { DeviceType } from "../device/device.model";

const OVERVIEW = "overview";
const GRAPHICS_VIEW = "graphics";

type TreeSearchResponse = {
  treeId: string;
  name: string;
  type: HierarchyType;
};

export class DeviceTreeNode extends TreeNode {
  deviceType: DeviceType;
  constructor(n, node) {
    super(n, node);
    this.deviceType = n.deviceType;
  }
}

// Fetchers

export const fetchNodes: FetchNodesFunction = async (
  { companyId, treeId, ...params },
  signal,
) => {
  const currentView = getActiveRoute(mainRoutes) as MainRoute;
  const ignoreNodes: string[] = [];
  if (currentView === OVERVIEW) {
    ignoreNodes.push(treeIdPrefix.device, treeIdPrefix.scadaGraphic);
  } else if (currentView !== GRAPHICS_VIEW) {
    ignoreNodes.push(treeIdPrefix.scadaGraphic, treeIdPrefix.deviceGraphic);
  }

  const url = treeId
    ? `analytics/companies/${companyId}/tree/node/${treeId}`
    : `analytics/companies/${companyId}/tree`;

  return assetsApiRequest
    .get<
      ApiResponse<NodeWithHierarchyDTO>
    >(url, { params: { ...params, ignoreNodes, fromGraphicMenuItem: true }, signal })
    .then((resp) => {
      const { currentNode, childNodes, parentNodes } = resp.data.data;

      // Create a chain of parents and also build relationship hierarchy in them
      // by setting parent nodes manually
      const parents = parentNodes.reduce((a, c, i) => {
        if (i === 0) {
          // TODO: How we are going to handle root levels is still up to discussion
          // Root is named "Back to root" for now
          a.push(new TreeNode({ ...c, name: t`Back to root` }));
        } else {
          // Set parent as the previous index in the parents array
          a.push(new TreeNode(c, a[i - 1]));
        }
        return a;
      }, [] as TreeNode[]);

      const node = currentNode
        ? new TreeNode(
            currentNode,
            parents.length > 0 ? parents[parents.length - 1] : undefined,
          )
        : undefined;

      return {
        parentNodes: parents,
        currentNode: node,
        childNodes: childNodes.map((n) =>
          n.type === nodeType.device
            ? new DeviceTreeNode(n, node)
            : new TreeNode(n, node),
        ),
      };
    });
};

export const searchNodes: SearchNodesFunction = async ({
  companyId,
  search,
}) => {
  return assetsApiRequest
    .get<
      ApiResponse<TreeSearchResponse[]>
    >(`analytics/companies/${companyId}/tree/search`, { params: { search } })
    .then((resp) => resp.data.data.map((level) => new TreeNode(level)));
};

export async function fetchGraphicChildNodes({
  companyId,
  node,
}: {
  companyId: number;
  node: TreeNode;
}): Promise<TreeNode[]> {
  return assetsApiRequest
    .get<ApiResponse<NodeWithHierarchyDTO>>(
      `analytics/companies/${companyId}/tree/node/${node.treeId}`,
      {
        params: { fromGraphicMenuItem: true },
      },
    )
    .then((resp) =>
      resp.data.data.childNodes.map((n) => new TreeNode(n, node)),
    );
}

export async function fetchClosestScadaNodeByGraphicsId({
  graphicsId,
  companyId,
}: {
  graphicsId: number;
  companyId: number;
}): Promise<TreeNode | undefined> {
  return assetsApiRequest
    .get<
      ApiResponse<TreeDTO | undefined>
    >(`analytics/companies/${companyId}/tree/node/${treeIdPrefix.scadaGraphicItem}${graphicsId}`)
    .then((resp) =>
      resp.data.data ? new TreeNode(resp.data.data) : undefined,
    );
}
